import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { authManager } from '../utils/auth';
import { ToastContainer, toast } from 'react-toastify';

const Base_Url = process.env.REACT_APP_API_BASE_URL;

const ServiceBenefitsView = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [benefit, setBenefit] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const fetchBenefitDetails = useCallback(async () => {
        try {
            const res = await authManager.authenticatedFetch(
                `${Base_Url}/membership/benefits/${id}`
            );
            const data = await res.json();

            if (data.status === 'success') {
                setBenefit(data.data);
            } else {
                toast.error('Failed to fetch benefit details');
            }
        } catch (error) {
            console.error('Error fetching benefit:', error);
            toast.error('Error fetching benefit details');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        fetchBenefitDetails();
    }, [id, fetchBenefitDetails]);

    const handleDelete = async () => {
        try {
            setDeleting(true);
            const res = await authManager.authenticatedFetch(
                `${Base_Url}/membership/benefits/${id}`,
                { method: 'DELETE' }
            );

            const data = await res.json();
            if (data.status === 'success') {
                toast.success('Benefit deleted successfully');
                setShowDeleteModal(false);
                setTimeout(() => navigate('/serviceBenefits'), 2000);
            } else {
                toast.error(data.message || 'Delete failed');
            }
        } catch (error) {
            console.error('Error deleting benefit:', error);
            toast.error('Error deleting benefit');
        } finally {
            setDeleting(false);
        }
    };

    if (loading) {
        return (
            <div className="text-center p-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">Loading...</span>
                </div>
            </div>
        );
    }

    if (!benefit) {
        return (
            <div className="text-center p-5">
                <p>Benefit not found</p>
            </div>
        );
    }

    const renderSection = (title, section) => {
        if (!section) return null;
        return (
            <div>
                <h5 className="f_blue pb-3 mb-3 border-bottom">{title}</h5>
                {section.serviceName && (
                    <div className="form-group row align-items-center mb-3">
                        <label className="col-12 col-md-4 col-form-label">Service Name</label>
                        <div className="col-12 col-md-8">{section.serviceName}</div>
                    </div>
                )}
                {section.title && (
                    <div className="form-group row align-items-center mb-3">
                        <label className="col-12 col-md-4 col-form-label">Title</label>
                        <div className="col-12 col-md-8">{section.title}</div>
                    </div>
                )}
                {section.description && (
                    <div className="form-group row mb-3">
                        <label className="col-12 col-md-4 col-form-label">Description</label>
                        <div className="col-12 col-md-8">{section.description}</div>
                    </div>
                )}
                {section.logoUrl && (
                    <div className="form-group row mb-3">
                        <label className="col-12 col-md-4 col-form-label">Logo</label>
                        <div className="col-12 col-md-8">
                            <div className="d-flex flex-row flex-wrap pt-20 pb-15">
                                <div className="community_imgwrapper m-3">
                                    <div className="community_icon">
                                        <img src={section.logoUrl} alt="Logo" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            <ToastContainer autoClose={3000} />
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2 align-items-center">
                        <div className="col-6 col-sm-6">
                            <h1 className="m-0 txt_lft">View Service Benefit</h1>
                        </div>
                        <div className="col-6 col-sm-6">
                            <ol className="breadcrumb justify-content-end">
                                <li className="breadcrumb-item">
                                    <Link to="/serviceBenefits">
                                        <i className="fa-solid fa-arrow-left-long" /> Back
                                    </Link>
                                </li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>

            <div className="content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="frmwdth_cntr">
                            <div className="col-lg-12">
                                <div className="card card-primary card-outline">
                                    <div className="card-body">
                                        {renderSection('Logos Section', benefit.logosSection)}
                                        {renderSection('CircleOne Benefits', benefit.circleOneBenefits)}
                                        {renderSection('Core Member Benefits', benefit.coreMemberBenefits)}

                                        {benefit.metadata && (
                                            <div>
                                                <h5 className="f_blue pb-3 mb-3 border-bottom">Metadata</h5>
                                                <div className="form-group row align-items-center mb-3">
                                                    <label className="col-12 col-md-4 col-form-label">Version</label>
                                                    <div className="col-12 col-md-8">{benefit.metadata.version}</div>
                                                </div>
                                                {benefit.metadata.notes && (
                                                    <div className="form-group row mb-3">
                                                        <label className="col-12 col-md-4 col-form-label">Notes</label>
                                                        <div className="col-12 col-md-8">{benefit.metadata.notes}</div>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        <div className="form-group row align-items-center mb-5">
                                            <label className="col-12 col-md-4 col-form-label">Status</label>
                                            <div className="col-12 col-md-8">
                                                <span className={`badge ${benefit.status === 'active' ? 'badge-success' : 'badge-danger'}`}>
                                                    {benefit.status}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="card-footer text-center">
                                        <Link to={`/serviceBenefitsAdd/${id}`} className="btn btn-blue">
                                            Edit
                                        </Link>
                                        <button
                                            onClick={() => setShowDeleteModal(true)}
                                            className="btn btn-danger ml-2"
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {showDeleteModal && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Confirm Delete</h5>
                                <button type="button" className="close" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
                                    <span>&times;</span>
                                </button>
                            </div>
                            <div className="modal-body">
                                <p>Are you sure you want to delete this benefit?</p>
                                <p className="text-muted small">This action cannot be undone.</p>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    onClick={handleDelete}
                                    disabled={deleting}
                                >
                                    {deleting ? 'Deleting...' : 'Delete'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ServiceBenefitsView;